<template>
  <div class="root">
    <div class="center">
      <h1>{{ $t("changePasswordPage.title") }}</h1>
      <p>
        {{ $t("changePasswordPage.description") }}<br />
        {{ $t("changePasswordPage.passwordRequirement") }}
      </p>
      <text-field
        type="password"
        :label="$t('changePasswordPage.currentPasswordLabel')"
        name="password-old"
        v-model="passwordOld"
      />
      <text-field
        type="password"
        :label="$t('changePasswordPage.newPasswordLabel')"
        name="password-new"
        v-model="passwordNew"
      />
      <text-field
        type="password"
        :label="$t('changePasswordPage.newPasswordConfirmLabel')"
        name="password-new-confirm"
        v-model="passwordNewConfirm"
      />
      <div v-if="message" class="info">{{ message }}</div>
      <button
        :disabled="processing || !passwordOld || !passwordNew || !passwordNewConfirm"
        class="button"
        @click="submit"
      >
        {{ $t("changePasswordPage.submitButton") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import TextField from "@/components/molecules/TextField.vue";
import { Auth } from "aws-amplify";
import { showNotifyDialog } from "@/lib/dialog";

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { TextField },
  data() {
    return {
      passwordOld: "",
      passwordNew: "",
      passwordNewConfirm: "",
      message: "",
      processing: false,
    };
  },
  methods: {
    async submit() {
      if (this.passwordNew !== this.passwordNewConfirm) {
        this.message = this.$t("changePasswordPage.errors.passwordMismatch") as string;
        return;
      }
      if (this.passwordNew.length < 7) {
        this.message = this.$t("changePasswordPage.errors.newPasswordTooShort") as string;
        return;
      }
      if (this.passwordOld.length < 7) {
        this.message = this.$t("changePasswordPage.errors.currentPasswordTooShort") as string;
        return;
      }

      this.processing = true;
      try {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.changePassword(user, this.passwordOld, this.passwordNew);
        this.message = "";
        await showNotifyDialog({ content: this.$t("changePasswordPage.successMessage") as string });
        this.$router.push({ name: "myPage" });
      } catch (error: any) {
        console.error(error);
        const { code } = error;
        if (code === "NotAuthorizedException") {
          this.message = this.$t("changePasswordPage.errors.incorrectPassword") as string;
          this.passwordOld = "";
        } else if (code === "InvalidPasswordException") {
          this.message = this.$t("changePasswordPage.errors.invalidPassword") as string;
        } else if (code === "LimitExceededException") {
          this.message = this.$t("changePasswordPage.errors.limitExceeded") as string;
        } else if (code === "NetworkError") {
          this.message = this.$t("changePasswordPage.errors.networkError") as string;
        } else {
          this.message = `${this.$t("changePasswordPage.errors.genericError")}${error.message || error}`;
        }
      } finally {
        this.processing = false;
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

h1 {
  font-size: var(--font-size-xl);
  margin-top: 30px;
  margin-bottom: 20px;
  color: var(--color-text-normal);
  border-bottom: solid 1px var(--color-border);
  @include mq(sp) {
    font-size: var(--font-size-md);
    margin-top: 0px;
    margin-bottom: 15px;
  }
}
p {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);
}
.root {
  position: relative;
  padding: 10px 0;
  width: 100%;
  height: 100vh;
  color: var(--color-text-on-fixed-bg);
}

.center {
  margin: 15px auto;
  padding: 30px 15px;
  width: 30%;
  min-width: 300px;
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  text-align: center;
  border-radius: var(--radius-sm);

  @include mq(sp) {
    margin: 0px auto;
    padding: 15px 0;
    width: 95%;
    min-width: none;
  }
}
.center form {
  padding: 15px 0;
  margin: auto;
  font-size: 16.1px;
  width: 50%;

  @include mq(sp) {
    width: 100%;
  }
}

.info {
  color: var(--color-warning);
}

.button {
  position: relative;
  margin-top: 15px;
  padding: 12px 50px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  font-size: var(--font-size-sm);
  color: var(--color-text-on-fixed-bg);
  border: none;
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    border: solid 1px var(--color-border);
    cursor: not-allowed;
    &::after {
      display: none;
    }
  }
}
</style>
